import { useMemo } from 'react';
import styles from './Header.module.scss';
import PrimaryMenu from '../../base/PrimaryMenu/PrimaryMenu';
import WithDevice from '../../../common/WithDevice/WithDevice';
import ProfileMenu from '../../base/ProfileMenu/ProfileMenu';
import { usePersonaMenuItems } from '../../../common/MenuItem/MenuItem.utils';
import { HeaderProps } from './Header.types';
import PureImage from '../../../common/Image/PureImage';
import ExpandMobileButton from './ExpandMobileButton';
import HeaderContext, { HeaderContextType } from '../../../../lib/context/HeaderContext';
import PureLink from '../../../common/Link/PureLink';
import { useId } from '../../../../lib/hooks/useId';
import useMobileExpanded from '../../../../lib/hooks/useMobileExpanded';
import SnackbarMessage from '../../base/SnackbarMessage/SnackbarMessage';
import { SCALE } from '../../../common/Image/ImageScale';
import DownloadsListCounter from './DownloadsListCounter';
import LocaleSwitcher from '../../base/LocaleSwitcher/LocaleSwitcher';

const Header = ({
  title,
  logoRef,
  primaryNavigation,
  profileNavigation,
  logoLink,
  regions,
  localeSwitcherLabel,
  localeSwitcherIcon,
}: HeaderProps) => {
  const [showMobileMenu, , setShowMobileMenu] = useMobileExpanded(false);
  const primaryItems = usePersonaMenuItems(primaryNavigation);
  const profileItems = usePersonaMenuItems(profileNavigation);

  const primaryId = useId('primary-menu-');
  const titleId = useId('title-');

  const headerContext: HeaderContextType = useMemo(
    () => ({
      showMobileMenu,
      setShowMobileMenu,
      primaryItems,
      profileItems,
      regions,
      localeSwitcherLabel,
      localeSwitcherIcon,
    }),
    [showMobileMenu, primaryItems, profileItems],
  );

  return (
    <HeaderContext value={headerContext}>
      <header>
        <div className={styles.headerWrapper}>
          <div className={styles.headerContainer}>
            <div className={styles.titleWrapper}>
              <ExpandMobileButton controllableId={primaryId} />
              {logoRef && (
                <PureLink href={logoLink} aria-labelledby={titleId}>
                  <PureImage
                    src={logoRef}
                    className={styles.logo}
                    imageClassName={styles.logoImage}
                    scale={SCALE.HEADER_LOGO}
                  />
                </PureLink>
              )}
              <h1 className={styles.title} id={titleId}>
                {title}
              </h1>
            </div>
            <WithDevice desktop>
              <div className={styles.accountDataContainer}>
                <LocaleSwitcher />
                <ProfileMenu />
                <DownloadsListCounter />
              </div>
            </WithDevice>
          </div>
        </div>
        <PrimaryMenu id={primaryId} />
        <SnackbarMessage />
      </header>
    </HeaderContext>
  );
};

export default Header;
